/**
 * MeProvider (TMMAPP-0003 / TMMAPP-0011). Loads `GET /v1/me` once per session and exposes the
 * normalized `Me`. A refetch failure keeps the last good session and surfaces a toast; the
 * blocking screen is only for the initial load. `tmm:session-locked` (from the SUBSCRIPTION_EXPIRED
 * interceptor) triggers a refetch so `locked` can take over; `tmm:standing-refused` (a role, grace or
 * locked refusal, TMMAPP-0110) refetches the served role flags and says why.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shell/NotifyProvider';
import { STANDING_REFUSED_EVENT } from '@/api/client';
import type { StandingRefusal } from '@/api/errors';
import { getMe, type Me } from '@/api/me';
import { ErrorState } from '@/components/ErrorState';
import { clearRetiredSetupStep } from '@/features/genesis/retiredSetupStep';
import { HiddenPageTitle } from '@/shell/PageHeader';
import { StandaloneFrame } from '@/shell/StandaloneFrame';
import { StandaloneMain } from '@/shell/StandaloneMain';
import { useBrand } from '@/theme/useBrand';
import { MeContext } from './meContextValue';

const LOCKED_EVENT = 'tmm:session-locked';

/**
 * What the session says for a refusal. An owner-only refusal's sentence is the caller's (it names the
 * action); a LOCKED one needs none — the refetched standing shows the locked state on the page.
 */
const STANDING_NOTICE: Partial<Record<StandingRefusal, string>> = {
  needManager: 'roles.needManager',
  readOnly: 'roles.readOnlyNow',
};

interface MeState {
  me: Me | null;
  loading: boolean;
  error: boolean;
  /** What the initial load threw — read by the blocking screen's `ErrorState`. */
  cause?: unknown;
  refetchError: boolean;
}

export function MeProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const notify = useNotify();
  const { productName } = useBrand();
  const [state, setState] = useState<MeState>({
    me: null,
    loading: true,
    error: false,
    refetchError: false,
  });
  const started = useRef(false);
  // The retired setup step is cleared on the session's first SUCCESSFUL read (TMMAPP-0143 item 4):
  // a failed first read and a Retry that succeeds still clear it.
  const clearedRetired = useRef(false);

  const load = useCallback(async () => {
    try {
      const me = await getMe();
      if (!clearedRetired.current) {
        clearedRetired.current = true;
        clearRetiredSetupStep(me.user.id);
      }
      setState({ me, loading: false, error: false, refetchError: false });
      return me;
    } catch (cause) {
      setState((prev) =>
        prev.me === null
          ? { me: null, loading: false, error: true, cause, refetchError: false }
          : { ...prev, loading: false, refetchError: true },
      );
    }
  }, []);

  const refetch = useCallback(async () => {
    return load();
  }, [load]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  useEffect(() => {
    const onLocked = () => {
      void refetch();
    };
    window.addEventListener(LOCKED_EVENT, onLocked);
    return () => window.removeEventListener(LOCKED_EVENT, onLocked);
  }, [refetch]);

  // A standing refusal (TMMAPP-0110): refetch the served role flags and say why, once.
  useEffect(() => {
    const onRefused = (event: Event) => {
      const notice = STANDING_NOTICE[(event as CustomEvent<StandingRefusal>).detail];
      void refetch();
      if (notice !== undefined) notify(t(notice), { severity: 'error' });
    };
    window.addEventListener(STANDING_REFUSED_EVENT, onRefused);
    return () => window.removeEventListener(STANDING_REFUSED_EVENT, onRefused);
  }, [refetch, notify, t]);

  const announcedRefetch = useRef(false);
  useEffect(() => {
    if (!state.refetchError) {
      announcedRefetch.current = false;
      return;
    }
    if (announcedRefetch.current) return;
    announcedRefetch.current = true;
    notify(t('session.refetchFailed'), {
      severity: 'error',
      action: { label: t('common.retry'), onClick: () => void refetch() },
    });
  }, [state.refetchError, notify, t, refetch]);

  const value = useMemo(() => (state.me ? { me: state.me, refetch } : null), [state.me, refetch]);

  if (value) {
    return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
  }

  if (state.error) {
    return (
      <StandaloneFrame>
        {/* The blocking screen's `h1` is the product (TMM-B7); the error state below is an `h2`. */}
        <HiddenPageTitle title={productName} />
        <ErrorState
          error={state.cause}
          what={t('errors.what.profile')}
          onRetry={() => {
            setState({ me: null, loading: true, error: false, refetchError: false });
            void load();
          }}
        />
      </StandaloneFrame>
    );
  }

  return (
    <StandaloneMain sx={{ py: 6 }}>
      <HiddenPageTitle title={productName} />
      <Typography variant="body1" role="status">
        {t('common.loading')}
      </Typography>
    </StandaloneMain>
  );
}
